import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EditIcon from '@mui/icons-material/Edit';
import { Spring } from '@/components/ui/Spring';
import styles from './styles.module.scss';

export interface ProfileAvatarProps {
  name: string;
  location?: string;
  avatarUrl?: string;
  onAvatarChange?: (file: File) => void;
  onAvatarRemove?: () => void;
  onEditClick?: () => void;
  className?: string;
}

export const ProfileAvatar: React.FC<ProfileAvatarProps> = ({
  name,
  location,
  avatarUrl,
  onAvatarChange,
  onAvatarRemove,
  onEditClick,
  className = '',
}) => {
  const { t } = useTranslation('account');
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const triggerInput = () => inputRef.current?.click();

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
      onAvatarChange?.(file);
    }
    setMenuOpen(false);
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    onAvatarRemove?.();
    setMenuOpen(false);
  };

  const displayUrl = previewUrl || avatarUrl;
  const avatarLetter = name.charAt(0).toUpperCase();

  return (
    <Spring className={`${styles.card} ${className}`}>
      <div className={styles.header}>
        <h3 className={styles.title}>{t('myProfile')}</h3>
        {onEditClick && (
          <button
            className={styles.editButton}
            onClick={onEditClick}
            aria-label={t('editProfile')}
          >
            <EditIcon fontSize="small" />
          </button>
        )}
      </div>
      <div className={`${styles.container} d-flex align-items-center`}>
        <div className={styles.wrapper}>
          <input
            type="file"
            accept="image/*"
            onChange={handleFile}
            ref={inputRef}
            hidden
          />
          <div>
            {displayUrl ? (
              <img className={styles.img} src={displayUrl} alt={name} />
            ) : (
              <div className={styles.imgPlaceholder}>
                <span>{avatarLetter}</span>
              </div>
            )}
          </div>
          <button
            className={styles.button}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Open menu"
          >
            <i className="icon-camera" />
          </button>
          {menuOpen && (
            <div className={styles.submenu}>
              <button onClick={triggerInput}>
                <i className="icon-upload" /> Upload
              </button>
              <button onClick={handleRemove}>
                <i className="icon-trash" /> Remove
              </button>
            </div>
          )}
        </div>
        <div className="d-flex flex-column g-4">
          <h3 className="text-overflow">{name}</h3>
          {location && <span className="text-overflow">{location}</span>}
        </div>
      </div>
    </Spring>
  );
};

export default ProfileAvatar;
